import { type IReaction, useGetConversationComments } from '@/api';
import { useCommentActions } from '@/api/hooks/useCommentActions';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { useAuthContext } from '@/contexts/auth';
import { CommentInput } from '@/features/comments/components/CommentInput';
import { CommentList } from '@/features/comments/components/CommentList';
import { useComment } from '@/features/comments/hooks/useComment';

interface CommentPanelProps {
	conversationId: string;
}

export const CommentPanel = ({ conversationId }: CommentPanelProps) => {
	const {
		items: comments,
		isLoading,
		hasMore,
		loadMoreRef,
	} = useComment(useGetConversationComments, conversationId);

	const { createComment, likeComment } = useCommentActions(conversationId);

	const handleNewComment = (content: string) => {
		createComment(content);
	};

	const { user } = useAuthContext();

	const handleLike = (commentId: number, data: IReaction) => {
		likeComment({ commentId, data });
	};

	const handleReply = (commentId: number) => {
		// Implement reply functionality
		// biome-ignore lint/suspicious/noConsole: <explanation>
		console.log('Replying to comment:', commentId);
	};

	if (isLoading) {
		return <LoadingSkeleton />;
	}

	return (
		<div className="w-full max-w-2xl mx-auto h-[700px] relative">
			<div className="overflow-y-auto h-full pb-20 ">
				<CommentList
					comments={comments}
					onLike={handleLike}
					onReply={handleReply}
				/>
				{hasMore && <div ref={loadMoreRef}>Loading more...</div>}
			</div>

			<div className="px-4 py-4 absolute bottom-0 left-0 right-0 bg-white">
				<CommentInput
					onSubmit={handleNewComment}
					author={{
						userId: user?.userId ?? '',
						userFullName: user?.fullName ?? '',
						userAvatar: user?.avatar ?? '/images/user.png',
					}}
				/>
			</div>
		</div>
	);
};
